import { createSlice } from '@reduxjs/toolkit';
import * as R from 'ramda';

// Utils
import http from 'utils/http';
import { userRoles } from 'utils/currentUserRoles';

const NOTIFICATION_EVENT_TYPES = {
  PTOFailed: 'ProductTransferOrderFailed',
  PTOReceived: 'ProductTransferOrderReceived',
  PTORejected: 'ProductTransferOrderRejected',
  supplierAuthExpiring: 'SupplierAuthorizationExpiring',
  twoPartyExchange: 'ExchangeNeedingConfirmation',
};

const initialState = {
  notifications: [],
};

const reducers = {
  setNotifications: R.assoc('notifications'),
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'notifications',
  // eslint-disable-next-line react-hooks/rules-of-hooks
  reducers: R.map(singleReducer => R.flip(R.useWith(singleReducer, [R.prop('payload')])), reducers),
});

export const { setNotifications } = actions;

const mapNotifications = response =>
  response.map(singleNotification => {
    switch (singleNotification.notificationEvent) {
      case NOTIFICATION_EVENT_TYPES.PTOReceived:
        return {
          affiliateId: singleNotification.notificationData.affiliateIdName,
          id: singleNotification.dataEntityId,
          ...singleNotification,
          notificationType: 'success',
          propName: 'ptoNumber',
        };
      case NOTIFICATION_EVENT_TYPES.PTORejected:
        return {
          affiliateId: singleNotification.notificationData.affiliateIdName,
          id: singleNotification.dataEntityId,
          ...singleNotification,
          notificationType: 'alert',
          propName: 'ptoNumber',
        };
      case NOTIFICATION_EVENT_TYPES.PTOFailed:
        return {
          affiliateId: singleNotification.notificationData.affiliateIdName,
          id: singleNotification.dataEntityId,
          ...singleNotification,
          notificationType: 'warning',
          propName: 'ptoNumber',
        };
      case NOTIFICATION_EVENT_TYPES.supplierAuthExpiring:
        return {
          affiliateId: singleNotification.notificationData.affiliateIdName,
          id: singleNotification.dataEntityId,
          ...singleNotification,
          notificationType: 'expire_warning',
          propName: 'productAuthorizationId',
        };
      case NOTIFICATION_EVENT_TYPES.twoPartyExchange:
        return {
          affiliateId: singleNotification.notificationData.affiliateIdName,
          id: singleNotification.dataEntityId,
          ...singleNotification,
          notificationType: 'two_party_warning',
          propName: 'dataEntityId',
          shortName: singleNotification.notificationData.inventoryOwnerName,
        };
      default:
        return singleNotification;
    }
  });

export const fetchNotifications = roles => dispatch => {
  const hasPtoManagerPermissions = R.includes(userRoles.PRODUCT_OWNERSHIP_MANAGE)(roles);

  // Permissions from CASREF-1408
  const hasInventoryOwnerLoadingPermissions = R.both(
    R.includes(userRoles.INVENTORY_OWNER),
    R.includes(userRoles.PRODUCT_LOADING_MANAGE)
  )(roles);

  // Permissions from CASREF-2130
  const hasSupplierLoadingPermissions = R.both(
    R.includes(userRoles.SUPPLIER),
    R.includes(userRoles.PRODUCT_LOADING_MANAGE)
  )(roles);

  if (
    !hasPtoManagerPermissions &&
    !hasSupplierLoadingPermissions &&
    !hasInventoryOwnerLoadingPermissions
  )
    return;

  http
    .get('/notifications/get-notifications')
    .then(({ data }) =>
      R.compose(result => dispatch(setNotifications(result)), mapNotifications)(data)
    );
};

export default reducer;
